import { useState } from 'react';

import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import { CircularProgress, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { NormalizedApiError } from '@/shared/api';

import { usePatchConversationEpisode } from '../../hooks';
import { VERSION_CONFLICT_CODE } from '../../model/types';

export interface EpisodeTopicEditorProps {
  conversationId: string;
  episodeId: string;
  topic: string | null;
  version: number;
  canManage: boolean;
  /** The version-conflict banner is owned by the page — this reports up, it never renders its own. */
  onVersionConflict: (sentVersion: number) => void;
  onSaved: () => void;
  /** Disabled (with a "Reload first" tooltip) while the page's conflict alert is showing. */
  disabled?: boolean;
  /** Same shape `OperatorComposer`/`ConversationHeader` use — anything other than a 409/422 is
   * reported here rather than swallowed. */
  notify: (severity: 'success' | 'error' | 'info' | 'warning', message: string) => void;
}

const TOPIC_MAX_LENGTH = 200;

/** `422`'s `details` is documented as an array of field errors (design §11.1: "the field's
 * `error`/`helperText` from `details[].message`") — read defensively, the shape is not generated
 * from an OpenAPI schema on this DTO. */
function firstDetailMessage(details: unknown): string | null {
  if (!Array.isArray(details)) {
    return null;
  }
  for (const entry of details) {
    if (
      entry &&
      typeof entry === 'object' &&
      typeof (entry as { message?: unknown }).message === 'string'
    ) {
      return (entry as { message: string }).message;
    }
  }
  return null;
}

/**
 * Inline topic rename — used both in the conversation header (the current episode) and in every
 * Episodes-card row (design §11.1). Read mode is text + a rename icon (hidden without `manage`); edit
 * mode is a bounded `TextField`, Enter saves, Esc cancels.
 */
export function EpisodeTopicEditor({
  conversationId,
  episodeId,
  topic,
  version,
  canManage,
  onVersionConflict,
  onSaved,
  disabled = false,
  notify,
}: EpisodeTopicEditorProps) {
  const { t } = useTranslation('conversations');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(topic ?? '');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const mutation = usePatchConversationEpisode(conversationId);

  function startEdit() {
    setDraft(topic ?? '');
    setSubmitError(null);
    setEditing(true);
  }
  function cancel() {
    setSubmitError(null);
    setEditing(false);
  }
  function save() {
    const trimmed = draft.trim();
    mutation.mutate(
      { episodeId, body: { topic: trimmed.length > 0 ? trimmed : null, version } },
      {
        onSuccess: () => {
          setSubmitError(null);
          setEditing(false);
          onSaved();
        },
        onError: (error: NormalizedApiError) => {
          if (error.status === 409 && error.code === VERSION_CONFLICT_CODE) {
            onVersionConflict(version);
            // Keep the editor open with the draft so the operator can re-apply after Reload.
            return;
          }
          if (error.status === 422) {
            setSubmitError(firstDetailMessage(error.details) ?? error.message);
            return;
          }
          // Any other failure (network, 500, …) used to be silently swallowed — the editor looked
          // like it saved when it had not (WP5 review). Report it like every other mutation here.
          notify('error', error.message);
        },
      },
    );
  }

  if (!editing) {
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography
          variant="body2"
          color={topic ? 'text.primary' : 'text.secondary'}
          fontStyle={topic ? undefined : 'italic'}
        >
          {topic ?? t('episodes.noTopic')}
        </Typography>
        {canManage ? (
          <Tooltip title={disabled ? t('detail.conflict.reloadFirst') : t('episodes.rename')}>
            <span>
              <IconButton
                size="small"
                aria-label={t('episodes.rename')}
                onClick={startEdit}
                disabled={disabled}
              >
                <EditRoundedIcon fontSize="inherit" />
              </IconButton>
            </span>
          </Tooltip>
        ) : null}
      </Stack>
    );
  }

  const tooLong = draft.length > TOPIC_MAX_LENGTH;
  const displayError = tooLong ? t('episodes.topicTooLong', { length: draft.length }) : submitError;
  const fieldId = `episode-topic-${episodeId}`;

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 220 }}>
      <TextField
        id={fieldId}
        size="small"
        fullWidth
        autoFocus
        value={draft}
        label={t('detail.header.topic')}
        placeholder={t('episodes.topicPlaceholder')}
        error={Boolean(displayError)}
        helperText={
          displayError ??
          (draft.length >= 180 ? t('episodes.topicCounter', { length: draft.length }) : undefined)
        }
        inputProps={{ maxLength: TOPIC_MAX_LENGTH + 20 }}
        onChange={(e) => {
          setSubmitError(null);
          setDraft(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (!tooLong) save();
          } else if (e.key === 'Escape') {
            cancel();
          }
        }}
      />
      <Tooltip title={t('episodes.saveTopic')}>
        <span>
          <IconButton
            size="small"
            color="primary"
            aria-label={t('episodes.saveTopic')}
            onClick={save}
            disabled={tooLong || mutation.isPending}
          >
            {mutation.isPending ? (
              <CircularProgress size={16} />
            ) : (
              <CheckRoundedIcon fontSize="inherit" />
            )}
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={t('episodes.cancelRename')}>
        <IconButton size="small" aria-label={t('episodes.cancelRename')} onClick={cancel}>
          <CloseRoundedIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}
